/**
 * Handler do formulário de 1 step para form-no-cep + LP de Ofertas (isOfferlp === true)
 * Combina em um único submit a validação que no multi-step fica dividida entre Step 1 e Step 2,
 * sem CEP e com Estado/Cidade sempre obrigatórios (cascata IBGE reaproveitada do multi-step).
 */

import { renderStepLayout } from '../renderers/ofertaFormularioMultiStepRenderer.js';
import { renderNoCepOfertaForm } from '../renderers/ofertaFormularioNoCepOfertaRenderer.js';
import { maskTelefone, maskCPF } from '../utils/formMasks.js';
import { validateCPF, isAgeValid, convertBirthdateToISO } from '../utils/formValidations.js';
import { sendFormData, buildUtmParams } from '../utils/formApi.js';
import { setSelectPlaceholder, loadStates, loadCitiesByState } from '../utils/ibgeLocation.js';
import { isMundoZeroKmHost } from '../utils/attributionUtils.js';
import { resolveOfferId } from '../helpers/ofertaFormularioHelpers.js';
import {
    createFieldErrorSetter,
    validateOptinCheckboxes,
    focusFirstInvalidField,
    isPhoneValid,
    EMAIL_REGEX,
    EMAIL_REQUIRED_MESSAGE,
    EMAIL_INVALID_MESSAGE,
    PHONE_INVALID_MESSAGE,
} from '../utils/formValidationUi.js';
import { OPTIN_FIELD_ORDER } from '../renderers/sharedCheckboxes.js';

/**
 * Ordem visual dos campos do formulário de 1 step (braço A), usada para focar o
 * primeiro campo inválido na tentativa de submit (AC-2).
 * @type {readonly string[]}
 */
const NO_CEP_FIELD_ORDER = Object.freeze([
    'name',
    'email',
    'phone',
    'cpf',
    'birthdate',
    'state',
    'city',
    ...OPTIN_FIELD_ORDER,
]);

export function setupNoCepOfertaHandlers(shadowRoot, config, ofertaData, showMessageFunc, hideMessageFunc) {
    const cssPrefix = config.cssPrefix;
    const setFieldError = createFieldErrorSetter(shadowRoot, cssPrefix);

    function isFullNameValid(nameValue) {
        const normalized = (nameValue || '').trim().replace(/\s+/g, ' ');
        const parts = normalized.split(' ').filter(Boolean);

        if (parts.length < 1) {
            return false;
        }

        return parts[0].length >= 2;
    }

    let formStartFired = false;
    let nameTouched = false;
    let emailTouched = false;
    let phoneTouched = false;
    let cpfTouched = false;
    let birthDateTouched = false;
    let stateTouched = false;
    let cityTouched = false;
    /** `name`s dos checkboxes de opt-in já interagidos pelo usuário. */
    const optinTouched = new Set();

    // Reaproveita o mesmo wrapper (col1/col2 + #message) do fluxo multi-step
    const container = shadowRoot.querySelector(`#${cssPrefix}-form-container`);
    container.innerHTML = renderStepLayout(config, ofertaData);

    const stepContent = shadowRoot.querySelector(`#${cssPrefix}-step-content`);
    stepContent.innerHTML = renderNoCepOfertaForm(config, ofertaData);

    const form = shadowRoot.querySelector(`#${cssPrefix}-form-no-cep-oferta`);
    const nameInput = shadowRoot.querySelector(`#${cssPrefix}-name`);
    const emailInput = shadowRoot.querySelector(`#${cssPrefix}-email`);
    const phoneInput = shadowRoot.querySelector(`#${cssPrefix}-phone`);
    const cpfInput = shadowRoot.querySelector(`#${cssPrefix}-cpf`);
    const birthDateInput = shadowRoot.querySelector(`#${cssPrefix}-birthdate`);
    const stateInput = shadowRoot.querySelector(`#${cssPrefix}-state`);
    const cityInput = shadowRoot.querySelector(`#${cssPrefix}-city`);

    /**
     * Valida todos os campos do formulário, marcando **todos** os inválidos
     * (nunca early return). O botão de envio não é mais desabilitado por
     * validade (AC-1): o bloqueio acontece no submit.
     * @param {{showErrors?: boolean}} [options] `showErrors` força a exibição de
     *   todas as mensagens (tentativa de submit); fora disso só campos já
     *   "tocados" exibem erro.
     * @returns {boolean}
     */
    function validateForm({ showErrors = false } = {}) {
        let isValid = true;

        const name = nameInput?.value.trim() || '';
        if (!isFullNameValid(name)) {
            isValid = false;
            if (showErrors || nameTouched) setFieldError('name', 'Preencha o nome.');
        } else {
            setFieldError('name', '');
        }

        const email = emailInput?.value.trim() || '';
        if (!EMAIL_REGEX.test(email)) {
            isValid = false;
            if (showErrors || emailTouched) setFieldError('email', email ? EMAIL_INVALID_MESSAGE : EMAIL_REQUIRED_MESSAGE);
        } else {
            setFieldError('email', '');
        }

        const phone = phoneInput?.value || '';
        if (!isPhoneValid(phone)) {
            isValid = false;
            if (showErrors || phoneTouched) setFieldError('phone', PHONE_INVALID_MESSAGE);
        } else {
            setFieldError('phone', '');
        }

        const cpfValue = cpfInput?.value || '';
        if (!cpfValue) {
            isValid = false;
            if (showErrors || cpfTouched) setFieldError('cpf', 'CPF é obrigatório.');
        } else if (cpfValue.length !== 14 || !validateCPF(cpfValue)) {
            isValid = false;
            if (showErrors || cpfTouched) setFieldError('cpf', 'CPF inválido.');
        } else {
            setFieldError('cpf', '');
        }

        if (birthDateInput) {
            const birthDateValue = birthDateInput.value || '';
            if (!birthDateValue) {
                isValid = false;
                if (showErrors || birthDateTouched) setFieldError('birthdate', 'Data de nascimento é obrigatória.');
            } else if (!isAgeValid(birthDateValue)) {
                isValid = false;
                if (showErrors || birthDateTouched) setFieldError('birthdate', 'É preciso ter mais de 18 anos para ter acesso a esta oferta.');
            } else {
                setFieldError('birthdate', '');
            }
        }

        if (!stateInput || stateInput.value.trim() === '') {
            isValid = false;
            if (showErrors || stateTouched) setFieldError('state', 'Selecione o estado.');
        } else {
            setFieldError('state', '');
        }

        if (!cityInput || cityInput.value.trim() === '') {
            isValid = false;
            if (showErrors || cityTouched) setFieldError('city', 'Informe a cidade.');
        } else {
            setFieldError('city', '');
        }

        // Cada opt-in desmarcado recebe a própria mensagem (AC-9), em vez do
        // bloqueio silencioso que existia aqui.
        if (!validateOptinCheckboxes(shadowRoot, cssPrefix, { showErrors, touched: optinTouched })) {
            isValid = false;
        }

        return isValid;
    }

    // Identificador canônico da oferta (SPEC.md > SDD "padronizacao-identificador-oferta-ga"
    // §4.1: hash/campaign_identifier → documentId → config.id → "sem-id") — mesmo usado no
    // envio do lead (ver sendFormData abaixo) e reportado no form_start pra permitir
    // correlacionar qual oferta estava sendo preenchida quando o usuário iniciou o formulário.
    const formStartOfertaId = resolveOfferId(ofertaData, config);

    // Dispara form_start ao completar (blur com conteúdo) o primeiro campo digitado;
    // campos atômicos (select/checkbox/radio) disparam no próprio "change".
    function fireFormStart(fieldName) {
        if (formStartFired) return;
        // Só marca como disparado depois de confirmar que o gtag está
        // disponível — senão, um blur antes do GTM carregar perderia o
        // form_start pra sempre nesta sessão (a flag ficava true sem nunca
        // ter enviado o evento).
        if (typeof window.gtag !== 'function') return;
        formStartFired = true;
        const formStartData = {
            form_id: 'form_widget_oferta_no_cep',
            form_field: fieldName,
            offer_id: formStartOfertaId,
            widget_type: 'form-no-cep',
        };

        // widget_version só é reportado dentro do portal mundozerokm.com.br.
        // Este handler é sempre a versão A (form-no-cep).
        if (isMundoZeroKmHost()) {
            formStartData.widget_version = 'A';
        }

        window.gtag('event', 'form_start', formStartData);
    }

    shadowRoot.addEventListener('focusout', (e) => {
        const el = e.target;
        if (formStartFired || !el) return;
        const tag = el.tagName;
        if (tag !== 'INPUT' && tag !== 'TEXTAREA') return;
        const type = (el.type || '').toLowerCase();
        if (type === 'checkbox' || type === 'radio') return;
        if (!el.value || !String(el.value).trim()) return;
        fireFormStart(el.name || el.id);
    });

    shadowRoot.addEventListener('change', (e) => {
        const el = e.target;
        if (formStartFired || !el) return;
        const tag = el.tagName;
        const type = (el.type || '').toLowerCase();
        if (tag === 'SELECT' || type === 'checkbox' || type === 'radio') {
            fireFormStart(el.name || el.id);
        }
    });

    // Campos de texto: o `input` nunca marca "touched" nem valida — só aplica
    // máscara quando o campo tem uma (AC-13: erro não aparece enquanto o campo
    // está em foco sendo digitado). Exibição de erro só no `blur` e no submit.
    nameInput?.addEventListener('blur', () => {
        nameTouched = true;
        validateForm();
    });

    emailInput?.addEventListener('blur', () => {
        emailTouched = true;
        validateForm();
    });

    phoneInput?.addEventListener('input', (e) => {
        e.target.value = maskTelefone(e.target.value);
    });
    phoneInput?.addEventListener('blur', () => {
        phoneTouched = true;
        validateForm();
    });

    cpfInput?.addEventListener('input', (e) => {
        e.target.value = maskCPF(e.target.value);
    });
    cpfInput?.addEventListener('blur', () => {
        cpfTouched = true;
        validateForm();
    });

    birthDateInput?.addEventListener('input', () => {
        birthDateTouched = true;
        validateForm();
    });
    birthDateInput?.addEventListener('change', () => {
        birthDateTouched = true;
        validateForm();
    });
    birthDateInput?.addEventListener('blur', () => {
        birthDateTouched = true;
        validateForm();
    });

    stateInput?.addEventListener('change', async () => {
        stateTouched = true;
        cityTouched = true;
        await loadCitiesByState(cityInput, stateInput.value, { logPrefix: '[NoCepOferta]' });
        validateForm();
    });

    cityInput?.addEventListener('change', () => {
        cityTouched = true;
        validateForm();
    });

    const checkboxes = shadowRoot.querySelectorAll(`#${cssPrefix}-checkboxes input[type="checkbox"]`);
    checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
            optinTouched.add(checkbox.name);
            validateForm();
        });
    });

    // Inicializa cascata Estado/Cidade (sempre presente neste fluxo)
    loadStates(stateInput, { logPrefix: '[NoCepOferta]' });
    if (cityInput) {
        setSelectPlaceholder(cityInput, 'Cidade*');
        cityInput.disabled = true;
    }

    validateForm();

    form?.addEventListener('submit', async (e) => {
        e.preventDefault();

        // Marca todos os campos inválidos e foca/rola até o primeiro (AC-2).
        if (!validateForm({ showErrors: true })) {
            focusFirstInvalidField(shadowRoot, cssPrefix, NO_CEP_FIELD_ORDER);
            return;
        }

        hideMessageFunc();

        const formData = new FormData(form);
        const completeData = {
            name: nameInput.value.trim(),
            email: emailInput.value.trim(),
            phone: phoneInput.value,
            cpf: cpfInput.value,
            state: stateInput.value,
            city: cityInput.value.trim(),
            birthDate: birthDateInput?.value ? convertBirthdateToISO(birthDateInput.value) : undefined,
        };

        if (formData.has('opt_in_widget')) {
            completeData.opt_in_widget = formData.get('opt_in_widget') === 'on';
        }
        if (formData.has('opt_in_aceite')) {
            completeData.opt_in_aceite = formData.get('opt_in_aceite') === 'on';
        }
        if (formData.has('opt_in_ciente')) {
            completeData.opt_in_ciente = formData.get('opt_in_ciente') === 'on';
        }
        if (formData.has('opt_in_declaro')) {
            completeData.opt_in_declaro = formData.get('opt_in_declaro') === 'on';
        }

        const utmParams = buildUtmParams(config, ofertaData, undefined, { formModel: 'form-no-cep' });

        const ofertaId = ofertaData?.hash || config.id || 'sem-id';

        try {
            showMessageFunc('Enviando dados...', 'info');

            if (typeof window.gtag === 'function') {
                const generateLeadData = {
                    event_category: 'Lead',
                    event_label: 'Form No-CEP Oferta Envio',
                    offer_id: resolveOfferId(ofertaData, config)
                };

                // widget_version só é reportado dentro do portal mundozerokm.com.br.
                // Este handler é sempre a versão A (form-no-cep).
                if (isMundoZeroKmHost()) {
                    generateLeadData.widget_version = 'A';
                }

                window.gtag('event', 'generate_lead', generateLeadData);
            }

            const response = await sendFormData(
                completeData,
                ofertaId,
                utmParams
            );

            if (response.ok || response.success) {
                if (typeof window.gtag === 'function') {
                    const leadData = {
                        event_category: 'Lead',
                        event_label: 'Motor v2 Success',
                        widget: config.titleForm,
                        offer_id: resolveOfferId(ofertaData, config)
                    };

                    // widget_version só é reportado dentro do portal mundozerokm.com.br.
                    // Este handler é sempre a versão A (form-no-cep).
                    if (isMundoZeroKmHost()) {
                        leadData.widget_version = 'A';
                    }

                    window.gtag('event', 'lead', leadData);
                }

                // Pixel de conversão OpenAI Ads — só no domínio próprio mundozerokm.com.br
                // (SDK já é inicializado pelo portal via RootLayout; sites de parceiros
                // nunca carregam o SDK, então window.oaiq nem existiria lá).
                if (typeof window.oaiq === 'function' && isMundoZeroKmHost()) {
                    window.oaiq('measure', 'lead_created', { type: 'customer_action' });
                }

                showMessageFunc(config.successMessage || 'Dados enviados com sucesso!', 'success');

                form.reset();
                nameTouched = false;
                emailTouched = false;
                phoneTouched = false;
                cpfTouched = false;
                birthDateTouched = false;
                stateTouched = false;
                cityTouched = false;
                optinTouched.clear();
                validateForm();
            } else {
                throw new Error('Erro no envio');
            }
        } catch (error) {
            console.error('Erro ao enviar formulário:', error);
            showMessageFunc(config.errorMessage || 'Erro ao enviar dados. Tente novamente.', 'error');
        }
    });
}
